// Centralized API client for the Seller Intelligence metric library harness.
// The backend routes are development-only and register just when FLASK_DEBUG or
// ENABLE_METRICS_PLAYGROUND is set, so every call here can 404 in a built env.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/metrics`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// Raised when the harness is not switched on, so the page can explain how to
// enable it instead of showing a bare network error.
export class PlaygroundDisabledError extends Error {
  constructor() {
    super('The metrics playground is not enabled on this backend.');
    this.disabled = true;
  }
}

const call = async (url, options) => {
  const res = await fetch(url, options);
  if (res.status === 404) {
    // A missing route means the harness is off; a known function returning 404
    // carries a JSON body naming it, which is a real error worth surfacing.
    const body = await res.json().catch(() => null);
    if (!body || !body.error) throw new PlaygroundDisabledError();
    throw new Error(body.error);
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

// The catalogue of runnable functions plus the sample store's default window.
export const getFunctions = (token) =>
  call(`${BASE}/functions`, { headers: authHeaders(token) });

// Sample inputs and config for one function, as editable JSON.
export const getSample = (token, name) =>
  call(`${BASE}/sample?function=${encodeURIComponent(name)}`, {
    headers: authHeaders(token),
  });

// Runs one function. Omitting inputs makes the backend fall back to the sample store.
export const runFunction = (token, body) =>
  call(`${BASE}/run`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });

// Runs one function on data fetched live from the connected Amazon account.
// Inputs are fetched server-side; only config and options apply.
export const runFunctionLive = (token, body) =>
  call(`${BASE}/run-live`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
